import type { NitroFetchOptions } from 'nitropack'
import { joinURL } from 'ufo'

export const useSimpleCommerce = () => {
    const apiUrl = joinURL(useRuntimeConfig().public.statamicUrl, '/!/simple-commerce')

    const getXsrfToken = () =>
        $fetch(joinURL(useRuntimeConfig().public.statamicUrl, '/api/csrf-cookie'), {
            credentials: 'include',
        })

    return async <T>(
        url: string,
        fetchOptions: NitroFetchOptions<string> = {},
        attemptNum = 0,
    ): Promise<T> => {
        try {
            let endpoint = joinURL(apiUrl, url)

            if (import.meta.dev) {
                const prefix = fetchOptions.method ?? 'Fetch'
                console.log(`[Simple Commerce ${prefix}]`, endpoint)
            }

            const headers: Record<string, any> = {
                ...fetchOptions.headers,
                Accept: 'application/json',
                'Content-Type': 'application/json',
            }

            const token = useCookie('XSRF-TOKEN')

            // ensure we have an XSRF token
            if (token.value) {
                if (import.meta.dev) {
                    console.log('[Simple Commerce] Using existing XSRF token')
                }
            } else {
                if (import.meta.dev) {
                    console.log('[Simple Commerce] Requesting XSRF token')
                }

                await getXsrfToken()
            }

            // add the token to the request headers
            headers['X-XSRF-TOKEN'] = token.value

            const credentialsSupported = 'credentials' in Request.prototype

            return await $fetch<T>(endpoint, {
                ...fetchOptions,
                credentials: credentialsSupported ? 'include' : undefined,
                headers,
            })
        } catch (error: any) {
            console.error('[Simple Commerce Error]', error)

            if (error.statusCode == 419 && attemptNum < 2) {
                if (import.meta.dev) {
                    console.log('[Simple Commerce] Requesting new XSRF token')
                }

                // session token has probably expired, request a new one
                await getXsrfToken()

                if (import.meta.dev) {
                    console.log(
                        `[Simple Commerce] Reattempting request (reattempt #${attemptNum + 1})`,
                    )
                }

                // reissue the request
                const simpleCommerce = useSimpleCommerce()
                return await simpleCommerce(url, fetchOptions, ++attemptNum)
            }

            throw createError({
                statusCode: error.response?.status,
                statusMessage: error.response?.statusText,
                data: error.response?._data,
            })
        }
    }
}
